import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import type { Company, NewCompany, UpdateCompany } from "@/api/queries";
import {
  useBusinessLines,
  useCreateCompany,
  useIndustries,
  useTradeShows,
  useUpdateCompany,
  useUploadCompanyLogo,
} from "@/api/queries";
import { Dialog } from "@/components/Dialog";
import { Button, FormField, Input, Select, Textarea } from "@/components/ui";

/**
 * Screen 08 of the Modernist design — *Agregar empresa* (.docs/08 §7 F1).
 *
 * Opened from Prospectos, and the only way a company is created in the SPA.
 * The nine statuses, the two taxonomies and the trade show are the model's own
 * fields; nothing here is invented, which is why §3 of the plan calls this
 * screen a straight translation.
 *
 * F3 gave it a second job. Screen 04 draws an *Editar datos generales* button
 * and no modal behind it, and F3 added `rfc`, `employees` and `notes` — three
 * fields the detail displays. A screen that shows an RFC with no way to enter
 * one is half a feature, so the same form edits as well as creates, with the
 * three new fields appearing only in edit mode: they are account detail nobody
 * has at the moment a prospect is first written down.
 */

const STATUS_OPTIONS = ["1", "2", "4", "5", "6", "7", "8", "9", "10"] as const;

/** The form's own shape. Strings throughout, because that is what inputs hold;
 *  the conversion to the API's types happens once, on submit. */
type Draft = {
  legal_name: string;
  name: string;
  website: string;
  rfc: string;
  employees: string;
  notes: string;
  status: string;
  trade_show: string;
  business_lines: number[];
  industries: number[];
  is_map_client: boolean;
};

const EMPTY: Draft = {
  legal_name: "",
  name: "",
  website: "",
  rfc: "",
  employees: "",
  notes: "",
  status: "1", // Interesado — the model's own default
  trade_show: "",
  business_lines: [],
  industries: [],
  is_map_client: false,
};

function draftFrom(company: Company | undefined): Draft {
  if (!company) return EMPTY;
  return {
    legal_name: company.legal_name,
    name: company.name,
    website: company.website ?? "",
    rfc: company.rfc ?? "",
    employees: company.employees ?? "",
    notes: company.notes ?? "",
    status: String(company.status ?? 1),
    trade_show: company.trade_show ? String(company.trade_show) : "",
    business_lines: [...(company.business_lines ?? [])],
    industries: [...(company.industries ?? [])],
    is_map_client: company.is_map_client ?? false,
  };
}

/** Two megabytes. A logo is a logo; anything larger is a scan of a brochure. */
const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export function NuevaEmpresa({
  open,
  onClose,
  company,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  /** Pass to edit rather than create — screen 04's *Editar datos generales*. */
  company?: Company;
  /**
   * Told what was created, for a host that carries on from it — the wrap-up of
   * a cold call, which opens the branch next. With it the modal stays where it
   * is instead of offering to go to the company's detail.
   */
  onCreated?: (company: Company) => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEdit = company !== undefined;
  const [draft, setDraft] = useState<Draft>(() => draftFrom(company));
  const [error, setError] = useState<string | null>(null);
  const create = useCreateCompany();
  const edit = useUpdateCompany(company?.id ?? 0);
  const uploadLogo = useUploadCompanyLogo(company?.id ?? 0);
  const pending = create.isPending || edit.isPending;

  const businessLines = useBusinessLines();
  const industries = useIndustries();
  const tradeShows = useTradeShows();

  // Reset on *open*, not on close: clearing while the dialog is still fading
  // out empties the fields in front of the seller who just saved.
  useEffect(() => {
    if (open) {
      setDraft(draftFrom(company));
      setError(null);
    }
  }, [open, company]);

  async function pickLogo(file: File | undefined) {
    if (!file) return;
    // Checked here rather than left to the server: a 400 after a slow upload
    // over a phone connection is a worse way to learn the file was too big.
    if (!file.type.startsWith("image/")) {
      setError(t("companyDetail.logoWrongType"));
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setError(t("companyDetail.logoTooBig"));
      return;
    }
    setError(null);
    try {
      await uploadLogo.mutateAsync(file);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("errors.saveCompany"));
    }
  }

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((previous) => ({ ...previous, [key]: value }));
  }

  async function save(thenAddBranch: boolean) {
    if (!draft.legal_name.trim()) {
      setError(t("newCompany.legalNameRequired"));
      return;
    }
    setError(null);
    const body: NewCompany = {
      legal_name: draft.legal_name.trim(),
      // The design treats the trade name as optional and most companies are
      // known by it; falling back to the legal name keeps every list from
      // rendering a blank first column.
      name: draft.name.trim() || draft.legal_name.trim(),
      website: draft.website.trim(),
      // The cast is narrow and true by construction: the only writer of
      // `draft.status` is the select below, whose options are `STATUS_OPTIONS`.
      status: Number(draft.status) as NonNullable<NewCompany["status"]>,
      trade_show: draft.trade_show ? Number(draft.trade_show) : null,
      business_lines: draft.business_lines,
      industries: draft.industries,
      is_map_client: draft.is_map_client,
      ...(isEdit
        ? {
            rfc: draft.rfc.trim(),
            employees: draft.employees.trim(),
            notes: draft.notes,
          }
        : {}),
    };
    try {
      if (isEdit) {
        await edit.mutateAsync(body as UpdateCompany);
        onClose();
        return;
      }
      const saved = await create.mutateAsync(body);
      onClose();
      onCreated?.(saved);
      // "Guardar y agregar sucursal" lands on the company detail, where F3 put
      // the branch modal.
      if (thenAddBranch) void navigate(`/prospectos/${saved.id}?sucursal=1`);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : t("errors.createCompany"));
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      kicker={t(isEdit ? "newCompany.editKicker" : "newCompany.kicker")}
      title={t(isEdit ? "newCompany.editTitle" : "newCompany.title")}
      footer={
        <>
          <Button variant="primary" disabled={pending} onClick={() => void save(false)}>
            {pending
              ? t("newCompany.saving")
              : t(isEdit ? "newCompany.saveChanges" : "newCompany.save")}
          </Button>
          {isEdit || onCreated ? null : (
            <Button disabled={pending} onClick={() => void save(true)}>
              {t("newCompany.saveAndBranch")}
            </Button>
          )}
          <Button variant="ghost" className="ml-auto" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </>
      }
    >
      {error ? (
        <p role="alert" className="mb-4 border-2 border-brand px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}

      <div className="grid gap-7 lg:grid-cols-[1fr_200px]">
        <div className="grid content-start gap-5 sm:grid-cols-2 sm:gap-x-6">
          <div className="sm:col-span-2">
            <FormField label={t("newCompany.legalName")}>
              {(id) => (
                <Input
                  id={id}
                  value={draft.legal_name}
                  maxLength={250}
                  required
                  // The field `Dialog` puts the cursor in on open — see the
                  // note there on why this is not React's `autoFocus`.
                  data-autofocus=""
                  placeholder={t("newCompany.legalNameExample")}
                  onChange={(event) => set("legal_name", event.target.value)}
                />
              )}
            </FormField>
          </div>

          <FormField label={t("newCompany.tradeName")}>
            {(id) => (
              <Input
                id={id}
                value={draft.name}
                maxLength={255}
                placeholder={t("newCompany.tradeNameExample")}
                onChange={(event) => set("name", event.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("newCompany.website")}>
            {(id) => (
              <Input
                id={id}
                type="url"
                value={draft.website}
                maxLength={180}
                placeholder={t("newCompany.websiteExample")}
                onChange={(event) => set("website", event.target.value)}
              />
            )}
          </FormField>

          <FormField label={t("newCompany.status")}>
            {(id) => (
              <Select
                id={id}
                value={draft.status}
                onChange={(event) => set("status", event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {t(`companies.status.${option}`)}
                  </option>
                ))}
              </Select>
            )}
          </FormField>

          <FormField label={t("newCompany.tradeShow")}>
            {(id) => (
              <Select
                id={id}
                value={draft.trade_show}
                onChange={(event) => set("trade_show", event.target.value)}
              >
                <option value="">{t("newCompany.noTradeShow")}</option>
                {(tradeShows.data?.results ?? []).map((show) => (
                  <option key={show.id} value={String(show.id)}>
                    {show.name}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </div>

        {/* Uploaded on pick, not on save. It is the one field that cannot ride
            in the JSON body — a `File` needs a multipart request with a
            browser-generated boundary — so it goes up on its own PATCH rather
            than turning the whole form into `FormData`. Which also means there
            is nothing to upload against until the company exists: on create
            the control says so instead of pretending. */}
        <FormField label={t("companyDetail.logo")} hint={t("companyDetail.logoHint")}>
          {(id) =>
            isEdit ? (
              <div className="flex flex-col items-start gap-3 border-2 border-dashed border-mute-400 bg-panel px-4 py-5">
                {company.logo ? (
                  <img
                    src={company.logo}
                    alt=""
                    className="max-h-16 w-auto"
                    aria-hidden="true"
                  />
                ) : null}
                <input
                  id={id}
                  type="file"
                  accept="image/*"
                  className="text-[11px]"
                  disabled={uploadLogo.isPending}
                  onChange={(event) => void pickLogo(event.target.files?.[0])}
                />
                {uploadLogo.isPending ? (
                  <span className="text-[11px] text-mute-600">
                    {t("companyDetail.logoUploading")}
                  </span>
                ) : null}
              </div>
            ) : (
              <p
                id={id}
                className="border-2 border-dashed border-mute-400 bg-panel px-4 py-5 text-[11px] text-mute-600"
              >
                {t("newCompany.logoPending")}
              </p>
            )
          }
        </FormField>
      </div>

      {isEdit ? (
        // Account detail, and only in edit mode: nobody has an RFC at the
        // moment a prospect is first written down, and three empty fields on
        // the create form would read as three things left undone.
        <div className="mt-6 grid gap-5 border-t-2 border-rule pt-6 sm:grid-cols-2 sm:gap-x-6">
          <FormField label={t("companyDetail.rfc")}>
            {(id) => (
              <Input
                id={id}
                value={draft.rfc}
                maxLength={13}
                className="uppercase"
                onChange={(event) => set("rfc", event.target.value.toUpperCase())}
              />
            )}
          </FormField>
          <FormField label={t("companyDetail.employees")}>
            {(id) => (
              <Input
                id={id}
                value={draft.employees}
                maxLength={40}
                onChange={(event) => set("employees", event.target.value)}
              />
            )}
          </FormField>
          <div className="sm:col-span-2">
            <FormField label={t("companyDetail.notes")}>
              {(id) => (
                <Textarea
                  id={id}
                  rows={3}
                  value={draft.notes}
                  className="resize-y font-sans"
                  onChange={(event) => set("notes", event.target.value)}
                />
              )}
            </FormField>
          </div>
        </div>
      ) : null}

      <div className="mt-6 grid gap-6 border-t-2 border-rule pt-6 sm:grid-cols-2">
        <ChipPicker
          label={t("newCompany.businessLines")}
          placeholder={t("newCompany.pickBusinessLine")}
          removeLabel={t("newCompany.removeBusinessLine")}
          options={businessLines.data?.results ?? []}
          selected={draft.business_lines}
          onChange={(next) => set("business_lines", next)}
        />
        <ChipPicker
          label={t("newCompany.industries")}
          placeholder={t("newCompany.pickIndustry")}
          removeLabel={t("newCompany.removeIndustry")}
          options={industries.data?.results ?? []}
          selected={draft.industries}
          onChange={(next) => set("industries", next)}
        />
      </div>

      <label className="mt-6 flex cursor-pointer items-center gap-2.5 text-sm">
        <input
          type="checkbox"
          className="h-[18px] w-[18px] accent-brand"
          checked={draft.is_map_client}
          onChange={(event) => set("is_map_client", event.target.checked)}
        />
        {t("newCompany.mapClient")}
      </label>
    </Dialog>
  );
}

/**
 * A select that adds, and chips that remove — the design's own pattern for
 * both taxonomies.
 *
 * Not a multi-select `<select multiple>`: on a phone that is a scrolling box
 * where ctrl-click is the only way to deselect, and what is currently chosen
 * is invisible the moment the list scrolls. Chips say what is chosen without
 * being asked.
 */
function ChipPicker({
  label,
  placeholder,
  removeLabel,
  options,
  selected,
  onChange,
}: {
  label: string;
  placeholder: string;
  removeLabel: string;
  options: readonly { id: number; name: string }[];
  selected: number[];
  onChange: (next: number[]) => void;
}) {
  const chosen = new Set(selected);
  const byId = new Map(options.map((option) => [option.id, option.name]));

  return (
    <FormField label={label}>
      {(id) => (
        <>
          <Select
            id={id}
            // Always back to the placeholder: the select is an "add" control,
            // not a statement of the current value — that is what the chips are.
            value=""
            onChange={(event) => {
              const picked = Number(event.target.value);
              if (picked && !chosen.has(picked)) onChange([...selected, picked]);
            }}
          >
            <option value="">{placeholder}</option>
            {options
              .filter((option) => !chosen.has(option.id))
              .map((option) => (
                <option key={option.id} value={String(option.id)}>
                  {option.name}
                </option>
              ))}
          </Select>
          {selected.length > 0 ? (
            <div className="mt-3 flex flex-wrap gap-2">
              {selected.map((value) => (
                <span
                  key={value}
                  className="inline-flex items-center gap-2 border-2 border-rule bg-panel py-[5px] pr-1.5 pl-3 text-xs"
                >
                  {byId.get(value) ?? `#${value}`}
                  <button
                    type="button"
                    aria-label={`${removeLabel}: ${byId.get(value) ?? value}`}
                    className="btn btn-ghost flex h-5 w-5 items-center justify-center p-0 text-brand-700"
                    onClick={() =>
                      onChange(selected.filter((entry) => entry !== value))
                    }
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      aria-hidden="true"
                    >
                      <path d="M18 6 6 18M6 6l12 12" />
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          ) : null}
        </>
      )}
    </FormField>
  );
}
